import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import * as C from 'app/utils/constants';

import {
  ExemplarPolarity,
  ExemplarProblem,
  FewShotExemplarFlow,
  getExemplars,
  isSameExemplar,
  termsMatch,
} from '../fewShotExemplarTypes';
import cs from './PromptView.module.scss';

interface Props {
  fewShotFlow: FewShotExemplarFlow;
}

// Each reads after a chip's name, e.g. "pool 2 is too big. …".
const PROBLEM_TEXT: Record<ExemplarProblem, string> = {
  outside: 'is outside the property. Move it onto the property, or remove it.',
  'too-small-imagery': 'is too small for this imagery. Make it bigger, or remove it.',
  'too-small-property':
    'is too small to find on a property this size. Make it bigger, or remove it.',
  'too-big': 'is too big. Draw around one object, or remove it.',
  'matches-example': 'is drawn over an example. Move it off the example, or remove it.',
};

const POLARITY_COPY: Record<
  ExemplarPolarity,
  {
    heading: string;
    swatchClassName: string;
    chip: string;
    drawButton: string;
    drawIcon: B.IconName;
    atMax: string;
    termLabel: string;
    termInfo: string;
    termPlaceholder: string;
  }
> = {
  positive: {
    heading: 'Examples',
    swatchClassName: cs.fewShotSwatchPositive,
    chip: 'Example',
    drawButton: 'Draw a box around an example',
    drawIcon: 'widget',
    atMax: `You can draw up to ${C.AGENT_FEW_SHOT_MAX_EXEMPLARS} examples.`,
    termLabel: 'Search term',
    termInfo:
      'A word the model looks for along with your boxes. Leave blank to search by boxes only.',
    termPlaceholder: 'e.g. swimming pool',
  },
  negative: {
    heading: 'Ignore',
    swatchClassName: cs.fewShotSwatchNegative,
    chip: 'Ignore',
    drawButton: 'Draw a box around something to ignore',
    drawIcon: 'minus',
    atMax: `You can draw up to ${C.AGENT_FEW_SHOT_MAX_EXEMPLARS} boxes to ignore.`,
    termLabel: 'Ignore term',
    termInfo:
      'A word for what the model should skip, along with your boxes. Leave blank to use boxes only.',
    termPlaceholder: 'e.g. pond',
  },
};

const POLARITIES: ExemplarPolarity[] = ['positive', 'negative'];

const FieldLabel: React.FC<{text: string; requirement: string; info: string}> = ({
  text,
  requirement,
  info,
}) => (
  <span className={cs.fewShotFieldLabel}>
    {text} <span className={B.Classes.TEXT_MUTED}>{requirement}</span>
    <B.Tooltip content={info}>
      <B.Icon icon="info-sign" size={12} tabIndex={0} className={cs.fewShotInfoIcon} />
    </B.Tooltip>
  </span>
);

const FewShotTips: React.FC = () => (
  <B.Popover
    className={cs.fewShotTipsTarget}
    placement="bottom-start"
    content={
      <ul className={cs.fewShotTips}>
        <li>Box one whole object, snug to its edges.</li>
        <li>Add 3–5 that differ in size, color, or lighting.</li>
      </ul>
    }
  >
    <button type="button" className={cs.fewShotTipsLink}>
      Tips for drawing good examples
    </button>
  </B.Popover>
);

// The note label, plus one section per polarity: draw button, its boxes as removable chips, and
// its term. The rectangle drawing itself happens on the map (see MonitorProjectView's Map.tsx).
const FewShotExemplarInput: React.FC<Props> = ({fewShotFlow}) => {
  const {
    fewShotState,
    exemplarProblems,
    setHoveredExemplar,
    setFewShotLabel,
    setSearchTerm,
    setNegativeLabel,
    startDrawingExemplar,
    stopDrawingExemplar,
    removeExemplar,
    focusExemplar,
    stopEditingExemplar,
    resetFewShot,
  } = fewShotFlow;
  const {label, searchTerm, negativeLabel, drawing, drawingPolarity, hovered, editing} =
    fewShotState;
  const isEditing = editing !== null;
  const termsClash = termsMatch(fewShotState);

  // Draw mode starts on, so the first box is one drag. Leaving this panel (another card, compare
  // mode, a past run) drops the boxes too: the map would keep drawing them over whatever is shown
  // next, and opening a run can switch the image they were drawn on.
  React.useEffect(() => {
    startDrawingExemplar('positive');
    return resetFewShot;
  }, [startDrawingExemplar, resetFewShot]);

  // Esc exits draw and edit mode; DrawLayer itself only handles Backspace (to delete a feature),
  // not Esc.
  React.useEffect(() => {
    if (!drawing && !isEditing) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        stopDrawingExemplar();
        stopEditingExemplar();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawing, isEditing, stopDrawingExemplar, stopEditingExemplar]);

  const renderSection = (polarity: ExemplarPolarity) => {
    const copy = POLARITY_COPY[polarity];
    const exemplars = getExemplars(fewShotState, polarity);
    const problems = exemplarProblems[polarity];
    const term = polarity === 'positive' ? searchTerm : negativeLabel;
    const chipName = (polarity === 'positive' ? label : negativeLabel).trim() || copy.chip;
    const firstProblemIndex = problems.findIndex((p) => p !== null);
    const firstProblem = problems[firstProblemIndex];
    const isActive = drawing && drawingPolarity === polarity;
    const showClash = polarity === 'negative' && termsClash;

    return (
      <div key={polarity} className={cs.fewShotSection}>
        <div className={cs.fewShotSectionHeading}>
          <span className={classnames(cs.fewShotSwatch, copy.swatchClassName)} />
          {copy.heading}
        </div>

        {exemplars.length >= C.AGENT_FEW_SHOT_MAX_EXEMPLARS ? (
          // Wrapped only when disabled: a disabled Tooltip still renders its wrapper span.
          <B.Tooltip className={cs.fewShotDrawButton} content={copy.atMax}>
            <B.Button text={copy.drawButton} icon={copy.drawIcon} disabled />
          </B.Tooltip>
        ) : (
          <B.Button
            className={cs.fewShotDrawButton}
            text={copy.drawButton}
            icon={copy.drawIcon}
            active={isActive}
            onClick={isActive ? stopDrawingExemplar : () => startDrawingExemplar(polarity)}
          />
        )}

        {exemplars.length > 0 && (
          <div className={cs.fewShotChips}>
            {exemplars.map((_, index) => {
              const ref = {polarity, index};
              const problem = problems[index];
              return (
                <B.Tag
                  key={index}
                  minimal
                  round
                  interactive
                  icon={problem ? 'warning-sign' : undefined}
                  active={isSameExemplar(ref, hovered) || isSameExemplar(ref, editing)}
                  intent={problem ? B.Intent.DANGER : B.Intent.NONE}
                  onClick={() => focusExemplar(ref)}
                  onMouseEnter={() => setHoveredExemplar(ref)}
                  onMouseLeave={() => setHoveredExemplar(null)}
                  onRemove={(e) => {
                    // The remove button sits inside the chip, whose click would open the next box.
                    e.stopPropagation();
                    removeExemplar(ref);
                  }}
                >
                  {`${chipName} ${index + 1}`}
                </B.Tag>
              );
            })}
          </div>
        )}
        {firstProblem && (
          <p className={cs.fewShotProblem}>
            {`${chipName} ${firstProblemIndex + 1} ${PROBLEM_TEXT[firstProblem]}`}
          </p>
        )}

        <B.FormGroup
          className={cs.fewShotLabel}
          label={<FieldLabel text={copy.termLabel} requirement="(optional)" info={copy.termInfo} />}
        >
          <B.InputGroup
            value={term}
            onChange={(e) =>
              (polarity === 'positive' ? setSearchTerm : setNegativeLabel)(e.target.value)
            }
            placeholder={copy.termPlaceholder}
            maxLength={C.AGENT_FEW_SHOT_MAX_LABEL_CHARS}
            intent={showClash ? B.Intent.DANGER : B.Intent.NONE}
          />
          {showClash && (
            <p className={classnames(cs.fewShotProblem, cs.fewShotTermProblem)}>
              The ignore term matches the search term.
            </p>
          )}
        </B.FormGroup>
      </div>
    );
  };

  return (
    <div className={cs.fewShotInput}>
      <p className={cs.fewShotIntro}>
        Draw a box around each thing you want to find. If something keeps matching that shouldn't,
        draw a box around it to ignore.
      </p>

      <B.FormGroup
        className={cs.fewShotLabel}
        label={<span className={cs.fewShotFieldLabel}>Note label</span>}
        labelInfo="(required)"
        helperText="Every note this run creates gets this name. The model doesn't see it."
      >
        <B.InputGroup
          value={label}
          onChange={(e) => setFewShotLabel(e.target.value)}
          placeholder="e.g. solar panel"
          maxLength={C.AGENT_FEW_SHOT_MAX_LABEL_CHARS}
        />
      </B.FormGroup>

      <hr className={cs.fewShotRule} />

      {POLARITIES.map(renderSection)}

      <FewShotTips />
    </div>
  );
};

export default FewShotExemplarInput;
